import { db } from '../../db/indexeddb'
import {
  getCloudTaskComments,
  getCloudTaskCommentIds,
  getCloudTaskCommentsByIds,
  getDeletedCloudTaskComments,
  createCloudTaskComment,
  updateCloudTaskComment,
  deleteCloudTaskComment,
} from '../../supabase/queries'
import { CloudTaskCommentSchema, CloudTaskCommentPayloadSchema } from '../sync-schemas'
import { resolveCloudTaskId, resolveLocalTaskId } from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { TaskComment } from '../../db/models'

/* ── TASK COMMENTS (Kommentare, #182) ─────────────────── */

export const taskCommentPullDescriptor: PullDescriptor<any> = {
  // `entityName + 's'` ist der Name in den Log-Zeilen; die Warteschlange heißt wie die Tabelle.
  entityName: 'taskComment',
  settingsKey: 'lastTaskCommentSyncTime',

  fetchRemote: (userId, since) => getCloudTaskComments(userId, since),
  fetchRemoteIds: (userId) => getCloudTaskCommentIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudTaskCommentsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudTaskComments(userId, since),

  safeParse: (raw) => CloudTaskCommentSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.taskComments,
  getAllLocal: () => db.taskComments.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.taskComments.filter(c => !c.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => {
    const localTaskId = await resolveLocalTaskId(remote.task_id, remote.task_local_id)
    // Die Aufgabe ist noch nicht auf diesem Gerät — der Task-Pull läuft vorher und legt sie an;
    // fehlt sie trotzdem, holt der nächste Zyklus den Kommentar nach.
    if (!localTaskId) return null
    return {
      id: crypto.randomUUID(),
      taskId: localTaskId,
      content: remote.content,
      postedAt: new Date(remote.posted_at),
      importedFrom: remote.imported_from ?? null,
      userId: remote.user_id,
      cloudId: remote.id,
      deletedAt: null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote) => {
    /*
      **Umgehängt** (Wiederkehr, ADR 0021): Ein anderes Gerät hat den Kommentar an den
      Nachfolger gehängt. Ist dessen Zeile noch nicht hier, bleibt der Kommentar vorerst,
      wo er ist — der nächste Zyklus hängt ihn um.
    */
    const localTaskId = await resolveLocalTaskId(remote.task_id, remote.task_local_id)
    return {
      ...(localTaskId ? { taskId: localTaskId } : {}),
      content: remote.content,
      postedAt: new Date(remote.posted_at),
      ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
      updatedAt: new Date(remote.updated_at),
      deletedAt: remote.deleted_at ? new Date(remote.deleted_at) : null,
    }
  },
}

export const taskCommentPushDescriptor: PushDescriptor<TaskComment> = {
  entityName: 'CloudTaskComment',
  getLocal: async (id) => (await db.taskComments.get(id)) ?? null,
  getLocalCloudId: (l) => l.cloudId,
  getLocalId: (l) => l.id,
  table: db.taskComments,

  // Ein Kommentar kann nicht vor seiner Aufgabe in der Cloud stehen — er wartet auf deren Kennung.
  canCreate: async (comment) => {
    const cloudTaskId = await resolveCloudTaskId(comment.taskId)
    return cloudTaskId ? { defer: false } : { defer: true }
  },

  // Auch beim Umhängen: Der Nachfolger muss oben sein, bevor der Kommentar auf ihn zeigt.
  canUpdate: async (comment) => {
    const cloudTaskId = await resolveCloudTaskId(comment.taskId)
    return cloudTaskId ? { defer: false } : { defer: true }
  },

  buildCreatePayload: async (comment) => payloadOf(comment),

  buildUpdatePayload: async (comment) => {
    const payload: Record<string, any> = await payloadOf(comment)
    // Das Rückgängig einer Löschung belebt die Zeile wieder — die `null` muss
    // ausdrücklich mit, sonst bleibt sie oben tot (Sync-Checkliste Punkt 17).
    if (!comment.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudTaskComment(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudTaskComment(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudTaskComment(cloudId, userId),
}

/** Alle Felder, immer — `task_id` trägt das Umhängen, `content` das Ändern. */
async function payloadOf(comment: TaskComment) {
  const cloudTaskId = await resolveCloudTaskId(comment.taskId)
  return CloudTaskCommentPayloadSchema.parse({
    task_id: cloudTaskId,
    task_local_id: comment.taskId,
    content: comment.content,
    posted_at: comment.postedAt.toISOString(),
    imported_from: comment.importedFrom ?? null,
    local_id: comment.id,
  })
}
